import { DevTool } from "@hookform/devtools";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import DataGrid from '../../common/dataGrid/dataGrid';
import ButtonWithSearch from '../../common/buttonWithSearch';
import { commonPostApi } from "../../../utils/apiService";
import { API_ENDPOINTS } from "../../../utils/apiEndpoints";
import InputPrimaryComponents from '../../common/inputPrimaryComponents';
import {
  arrageArrayFunc,
  convertToSearchField,
  generateColumnMetadata,
  Feature,
} from "../../../utils/utils";
import PrimaryHeading from '../../common/primaryHeading';
import PrimarySideBar from '../../common/sideBar/primarySideBar';
import AddButtonSection from '../../common/AddButtonSection';
import CommonModal from '../../common/commonModel';
import { showToast } from '../../common/toast';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../../constants/toast";
import { SORT_ORDER } from "../../../constants/sortOrder";
import { useSelector } from "react-redux";
import "../../../assets/styles/roleuser.scss";
import { RolesandPermissionsTableColumns } from "../../../utils/serviceData";
export default function RoleUserView({ selectedRole, ...props }) {
  const campId = useSelector((state) => state.settings.camp?.camp_details_id);
  const campName = useSelector((state) => state.settings.camp?.camp_name);
  const tenantId = useSelector((state) => state.common.organization?.tenant_id);
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      camps: campName || null   // 👈 default selected camp
    }
  });
  const menuLeft = useRef(null);
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [assignUserVisible, setAssignUserVisible] = useState(false);
  const [visible, setVisible] = useState({});
  const searchFields = ["user_name", "email", "phone_no", "address"];
  const [selectedUser, setSelectedUser] = useState(null);
  const [items, setItems] = useState([]);
  const [user, setUser] = useState([]);
  const [camps, setCamps] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [searchNotFound, setSearchNotFound] = useState(false);
  const selectedCamp = watch("camps");
  const [removeID, setRemoveID] = useState("");
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "user_name",
    sortDir: SORT_ORDER.ASC,
    searchCriteria: convertToSearchField(searchFields, ""),
  });

  function onPageChange(event) {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      first: event?.first,
      page: event.page + 1,
    }));
    init({
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    });
  }

  const init = async (navigator) => {
    setInitLoading(true);
    setCamps([campName]);
    let currentObj = {
      query: {
        page: navigator?.page,
        limit: navigator?.rows,
        searchCriteria: {
          ...navigator?.searchCriteria,
          role_id: selectedRole?.app_role_id,
          camp_details_id: campId,
        },
      },
    };

    Promise.all([commonPostApi(API_ENDPOINTS.GET_ROLE_BASED_USERS, currentObj)])
      .then(([data]) => {
        const userData = data?.data?.data || [];

        setNavigator((prev) => ({
          ...prev,
          totalRecords: data?.data?.total || 0,
          page: data?.data?.currentPage,
        }));

        setColumns(
          arrageArrayFunc(generateColumnMetadata({ data: userData, defaultColumnsArr: RolesandPermissionsTableColumns }), [
            "user_name",
            "email",
            "phone_no",
            "address",
          ])
        );
        setValues(userData);
        setLoading(false);
        setInitLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        setInitLoading(false);
        setColumns([]);
        setValues([]);
        setNavigator((prev) => ({
          ...prev,
          totalRecords: 0,
        }));
      });
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  const onRowCountChange = (e) => {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    }));
    init({
      ...navigator,
      page: 1,
      first: 0,
      rows: e?.target?.value,
    });
  };

  const onSubmit = async (data) => {
    if (loading) return;

    if (!selectedUser) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.SELECT_A_USER);
      return;
    }

    if (!data?.camps) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.SELECT_A_CAMP);
      return;
    }

    setLoading(true);
    let headers = {
      "Content-Type": "application/json",
    };

    let assignUser = {
      user_id: selectedUser,
      app_role_id: selectedRole?.app_role_id,
      tenant_id: tenantId,
      camp_details_id: campId,
    };
    await commonPostApi(API_ENDPOINTS.ADD_USER_TO_ROLE, assignUser, {
      headers,
    })
      .then((data) => {
        if (data?.status === 201) {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.USER.ADDED);
          reset();
          setAssignUserVisible(false);
          setInputValue("");
          init({
            ...navigator,
            page: 1,
            searchCriteria: convertToSearchField(searchFields, ""),
          });
          setSelectedUser(null);
          setLoading(false);
        } else if (
          data?.data?.message ===
          "User is already assigned to this role."
        ) {
          showToast(TOAST_TYPE.ERROR, FEATURE_MSG.USER.ALREADY_ASSIGNED);
          setLoading(false);
        } else {
          showToast(TOAST_TYPE.ERROR, data?.message ?? COMMON_MSG.ERROR.GENERIC);
          setLoading(false);
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
        setLoading(false);
      });
  };

  const onDelete = async () => {
    if (loading) return;
    setLoading(true);
    let headers = {
      "Content-Type": "application/json",
    };
    let removeUser = {
      camp_details_id: campId,
      app_role_id: [selectedRole?.app_role_id],
      user_id: removeID,
    };
    await commonPostApi(API_ENDPOINTS.REMOVE_USER, removeUser, {
      headers,
    })
      .then((data) => {
        if (data?.status === 200) {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.ROLE.USER_REMOVED);
          init({
            ...navigator,
            page: 1,
            searchCriteria: convertToSearchField(searchFields, ""),
          });
          setLoading(false);
          setVisible(false);
        } else {
          showToast(TOAST_TYPE.ERROR, data?.message ?? COMMON_MSG.ERROR.GENERIC);
          setLoading(false);
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
        setLoading(false);
      });
  };

  const button = [
    {
      type: "primarySuccessButton",
      label: "Assign User",
      class: "mt-2 w-full flex pr-5",
      onClick: () => {
        setAssignUserVisible(true);
        setInputValue("");
        setSelectedUser(null);
        setSearchNotFound(false);
      },
    },
  ];

  const AssignUserbuttons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      onClick: handleSubmit(onSubmit),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        reset();
        setAssignUserVisible(false);
      },
    },
  ];
  const itemRenderer = (item, props) => (
    <div className="p-menuitem-content">
      {item.label === "Delete" && (
        <Button
          className="card-button-delete"
          label="Remove User"
          onClick={() => {
            setRemoveID(props?.props?.itemProp?.user_id);
            setVisible({
              show: true,
            });
          }}
        />
      )}
    </div>
  );

  const submitButtons = [
    {
      type: "primarySuccessButton",
      label: "Yes",
      class: "w-8rem pr-3",
      onClick: () => {
        onDelete();
      },
    },
    {
      type: "secondaryButton",
      label: "No",
      class: "w-7rem",
      onClick: () => {
        setVisible((prev) => ({
          ...prev,
          show: false,
        }));
      },
    },
  ];

  const getUser = async () => {
    await commonPostApi(API_ENDPOINTS.GET_ALL_USERS, {})
      .then((data) => {
        if (data?.status === 200) {
          const alluser = data?.data?.data;
          let userDetail = [];
          alluser.forEach((item) =>
            userDetail.push({
              user_name: item?.user_name,
              user_id: item?.user_id,
            })
          );
          setUser(userDetail);
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      });
  };


  const search = (event) => {
    const query = event.query.toLowerCase().trim();
    const filtered = user
      ?.filter((user) => user?.user_name?.toLowerCase().includes(query))
      ?.map((user) => ({ label: user?.user_name, value: user?.user_id }));
    setItems(filtered);
    filtered.length > 0 ? setSearchNotFound(false) : setSearchNotFound(true);
  };

  function handleSearchChange(value) {
    setNavigator((prev) => ({
      ...prev,
      searchCriteria: convertToSearchField(searchFields, value),
    }));
    init({
      ...navigator,
      searchCriteria: convertToSearchField(searchFields, value),
    });
  }
  useEffect(() => {
    init(navigator);
    getUser();
  }, []);

  return (
    <div className="roleuser-container container">
      <PrimaryHeading className={"mt-3 mb-3"} />
      <DataGrid
        header={
          <ButtonWithSearch
            buttons={button}
            backButton={props?.backButton}
            searchHeaderLable={selectedRole && selectedRole?.role_name}
            searchPlaceHolder={"Search Users"}
            isSearchEnabled={true}
            isCalenderEnabled={false}
            isButtonEnabledLeft={false}
            isButtonEnabledRight={Feature?.featureVerify(
              Feature.listOfFeature.userAdd
            )}
            loading={initLoading}
            styles={{ gridTemplateColumns: "1fr 400px" }}
            onChange={handleSearchChange}
          />
        }
        onSortClick={onSortClick}
        onPageChange={onPageChange}
        onRowCountChange={onRowCountChange}
        sortable={true}
        isPaginatorEnabled={true}
        navigator={navigator}
        column={columns}
        value={values}
        ref={menuLeft}
        loading={loading}
        optionsView={[
          {
            items: [
              {
                label: "Delete",
                icon: "pi pi-upload",
                template: itemRenderer,
              },
            ],
          },
        ]}
      />
      {assignUserVisible && (
        <PrimarySideBar
          visible={assignUserVisible}
          onHide={() => {
            reset();
            setAssignUserVisible(false)
          }}
          header={<div className="sideBar-header">Assign Role</div>}
        >
          <InputPrimaryComponents
            inter
            type="autocomplete"
            label="User Search"
            name="assign_user"
            placeholder="Type to search"
            searchIcon
            required
            field="label"
            paddingStyle={true}
            items={items}
            value={inputValue}
            search={search}
            onChange={(e) => {
              setInputValue(e);
              if (e?.value) {
                setSelectedUser(e?.value);
              } else if (e === "") {
                setSelectedUser(null);
              }
            }}
          />
          <div className="mt-2">

            <InputPrimaryComponents
              control={control}
              errors={errors}
              type="dropdown"
              label="Camps"
              name="camps"
              className="mt-2"
              placeholder="Select Camp"
              required
              options={camps}
              rules={{ required: "Camp selection is required" }}
              paddingStyle={true}
            />
          </div>

          {searchNotFound && inputValue.length > 0 && (
            <small className="text-red-600 inter-400">User Not Found</small>
          )}
          <div className="pt-3">
            <AddButtonSection
              disabled={loading || !selectedUser || !selectedCamp}
              buttons={AssignUserbuttons}
            />
          </div>
        </PrimarySideBar>
      )}
      <CommonModal
        visible={visible?.show}
        header="User Patient"
        onHide={() => setVisible(false)}
      >
        <div>
          <div className="flex justify-content-center pr-4">
            Would you like to delete this user?
          </div>

          <div className="mt-6 ml-2">
            <AddButtonSection buttons={submitButtons} />
          </div>
        </div>
      </CommonModal>
      <DevTool control={control} />
    </div>
  );
}
